import { Body1, Link, Spinner, Switch, Text, makeStyles, tokens } from '@fluentui/react-components';
import { ChevronRightRegular } from '@fluentui/react-icons';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AlertType } from '../../libs/models/AlertType';
import { useUserMemory } from '../../libs/hooks/useUserMemory';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { addAlert } from '../../redux/features/app/appSlice';
import { MemoryDeleteConfirmDialog } from './MemoryDeleteConfirmDialog';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    description: {
        color: tokens.colorNeutralForeground3,
    },
    // Layout only — Fluent UI's Link owns the colour and hover underline. Rendered as a button (no
    // href) because it navigates within the SPA rather than pointing at a URL.
    openLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
        width: 'fit-content',
    },
    hint: {
        color: tokens.colorNeutralForeground3,
    },
});

/**
 * Settings section for per-user long-term memory (#2220): the opt-in toggle plus a link through to the
 * dedicated Minnen page where the stored notes are read and deleted. Keeping only the toggle here
 * (the switch is a setting) and moving the note list out (that's data management) keeps Settings from
 * getting crowded as notes accumulate. Rendered only when the UserMemoryEnabled flag is on (SettingsView
 * gates it), and fetches the current state on mount.
 */
export const UserMemorySection: React.FC = () => {
    const classes = useClasses();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const memory = useAppSelector((state: RootState) => state.app.userMemory);
    const { fetchMemory, updateOptIn } = useUserMemory();

    const [initialized, setInitialized] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [optInBusy, setOptInBusy] = useState(false);
    const [optOutBusy, setOptOutBusy] = useState(false);
    const [confirmOptOut, setConfirmOptOut] = useState(false);

    // The hook recreates fetchMemory on every silent token renewal, so this effect re-runs while the
    // user sits on Settings. Track first-load completion in a ref (readable inside the async callback
    // without re-triggering the effect) so a background refetch never re-shows the spinner or, on a
    // transient failure, flips the toggle back to its locked/unknown state over an already-known value.
    const initializedRef = useRef(false);

    useEffect(() => {
        let active = true;
        void fetchMemory().then((result) => {
            if (!active) {
                return;
            }
            if (result === undefined) {
                if (!initializedRef.current) {
                    setLoadError(true);
                }
            } else {
                setLoadError(false);
            }
            initializedRef.current = true;
            setInitialized(true);
        });
        return () => {
            active = false;
        };
    }, [fetchMemory]);

    const applyOptIn = (checked: boolean): Promise<void> => {
        setOptInBusy(true);
        return updateOptIn(checked)
            .catch(() => {
                // The hook logs + reverts the optimistic toggle; surface a user-facing error too so a
                // failed consent change isn't silent.
                dispatch(addAlert({ message: t('settings.memory.optInError'), type: AlertType.Error }));
            })
            .finally(() => {
                setOptInBusy(false);
            });
    };

    const onToggle = (_: unknown, data: { checked: boolean }) => {
        // Opting out purges every stored note server-side, so make that destructive step explicit —
        // route it through the confirm dialog — instead of erasing on a single toggle click. Opting in,
        // or opting out with nothing stored, destroys no data, so apply it immediately. The Switch is
        // controlled by memory.optedIn, so deferring the write here leaves it visually unchanged until
        // the user confirms (and untouched if they cancel).
        if (!data.checked && memory.notes.length > 0) {
            setConfirmOptOut(true);
            return;
        }
        void applyOptIn(data.checked);
    };

    const onConfirmOptOut = () => {
        setOptOutBusy(true);
        updateOptIn(false)
            .catch(() => {
                // A data-removal action must not fail silently — the toggle stays on if it didn't take.
                dispatch(addAlert({ message: t('settings.memory.optInError'), type: AlertType.Error }));
            })
            .finally(() => {
                setOptOutBusy(false);
                setConfirmOptOut(false);
            });
    };

    const noteCount = memory.notes.length;

    return (
        <div className={classes.root}>
            <Text weight="semibold">{t('settings.memory.sectionTitle')}</Text>
            <Body1 className={classes.description}>{t('settings.memory.description')}</Body1>

            <Switch
                checked={memory.optedIn}
                onChange={onToggle}
                // Locked until the first load resolves with known state — a failed load (loadError)
                // leaves the true server value unknown, so keep it locked rather than exposing the
                // default checked={false} as if it were real — and while an opt-in/opt-out write is in
                // flight (so a rapid re-toggle can't stack requests).
                disabled={!initialized || loadError || optInBusy || optOutBusy}
                label={t('settings.memory.optInLabel')}
                data-testid="memoryOptInSwitch"
            />

            {!initialized && <Spinner size="tiny" labelPosition="after" label={t('settings.memory.loading')} />}

            {initialized && loadError && <Body1 className={classes.hint}>{t('settings.memory.error')}</Body1>}

            {initialized && !loadError && noteCount === 0 && !memory.optedIn && (
                <Body1 className={classes.hint}>{t('settings.memory.emptyOptedOut')}</Body1>
            )}

            {/* Link through to the notes page only when something is stored — no notes, nothing to open.
                Residual notes can exist under an off toggle (an opt-out purges only the current company's
                scope), so this is gated on note count, not the opt-in flag, to keep those reachable. */}
            {initialized && !loadError && noteCount > 0 && (
                <Link
                    as="button"
                    className={classes.openLink}
                    onClick={() => {
                        void navigate('/memories');
                    }}
                    data-testid="memoryOpenNotes"
                >
                    {t('settings.memory.notesTitle')} ({noteCount})
                    <ChevronRightRegular />
                </Link>
            )}

            <MemoryDeleteConfirmDialog
                open={confirmOptOut}
                mode="optOut"
                busy={optOutBusy}
                onConfirm={onConfirmOptOut}
                onCancel={() => {
                    if (!optOutBusy) {
                        setConfirmOptOut(false);
                    }
                }}
            />
        </div>
    );
};
